/**
 * The material roster. 88 materials across 7 categories, four of them one-way
 * semi-solids (see the SEMI-SOLID block at the end).
 *
 * Authoring notes:
 *  - `tags` are load-bearing. Reactions are written against tags (see
 *    reactions.ts), so a material's tags ARE its interaction surface. Declare
 *    them honestly and a new material inherits ~30 interactions for free.
 *  - density drives displacement. Air=1, water=1000. Oil<water so oil floats;
 *    mercury >> everything so mercury sinks through anything.
 *  - Transition targets are string names resolved in finalizeMaterials(), so
 *    declaration order doesn't matter for references.
 */

import {
  Category,
  defineMaterial,
  finalizeMaterials,
  materialCount,
  tags,
  tryMatId,
  type MaterialSpec,
} from './materials';

const rigid = (n: string, l: string, s: MaterialSpec) => defineMaterial(n, l, Category.RIGID, s);
const loose = (n: string, l: string, s: MaterialSpec) => defineMaterial(n, l, Category.LOOSE, s);
const liquid = (n: string, l: string, s: MaterialSpec) => defineMaterial(n, l, Category.LIQUID, s);
const gas = (n: string, l: string, s: MaterialSpec) => defineMaterial(n, l, Category.GAS, s);
const sludge = (n: string, l: string, s: MaterialSpec) => defineMaterial(n, l, Category.SLUDGE, s);
const plasma = (n: string, l: string, s: MaterialSpec) => defineMaterial(n, l, Category.PLASMA, s);
const field = (n: string, l: string, s: MaterialSpec) => defineMaterial(n, l, Category.FIELD, s);

let registered = false;

export function registerMaterials(): void {
  if (registered) return;
  registered = true;

  // id 0 — must be first.
  defineMaterial('VOID', 'Air', Category.VOID, {
    color: 0x000000,
    density: 1,
    alpha: 0,
    conductivity: 0.02,
    heatCapacity: 0.25,
    hardness: 0,
    desc: 'Empty space. Carries heat poorly and pressure well.',
  });

  // ------------------------------------------------------------ RIGID

  rigid('GRANITE', 'Granite', {
    color: 0x5a5750, tags: tags('STONE', 'INSULATOR', 'SEALED'),
    density: 2700, hardness: 7, brittleness: 0.25, friction: 0.95, mass: 2.7,
    conductivity: 0.12, heatCapacity: 2.2, corrosionResist: 0.8,
    ruptureThreshold: 900, shatteredInto: 'GRAVEL', grain: 0.22, colorVariance: 0.1,
    desc: 'Deep stone. Resists acid and heat; yields only to force.',
  });

  rigid('LIMESTONE', 'Limestone', {
    color: 0x8c8272, tags: tags('STONE', 'POROUS', 'SEALED'),
    density: 2400, hardness: 4, brittleness: 0.4, friction: 0.9, mass: 2.4,
    conductivity: 0.14, heatCapacity: 1.8, corrosionResist: 0.15,
    ruptureThreshold: 500, shatteredInto: 'GRAVEL', grain: 0.18,
    desc: 'Soft sedimentary stone. Acid eats through it; water seeps into it.',
  });

  rigid('SANDSTONE', 'Sandstone', {
    color: 0xa8895e, tags: tags('STONE', 'POROUS'),
    density: 2200, hardness: 3, brittleness: 0.55, friction: 0.92, mass: 2.2,
    conductivity: 0.13, heatCapacity: 1.7, corrosionResist: 0.2,
    ruptureThreshold: 380, shatteredInto: 'SAND', grain: 0.3,
    desc: 'Compressed sand. Crumbles back into what it came from.',
  });

  rigid('OBSIDIAN', 'Obsidian', {
    color: 0x2a2f3a, tags: tags('STONE', 'CERAMIC', 'INSULATOR', 'SEALED'),
    // hardness 8 -> 7 (#132, docs/15 §3.2). An authoring correction that also
    // closes a door. It was the second-hardest material in the game, above
    // granite, while carrying `brittleness: 0.85`, `shatteredInto: GRAVEL` and
    // a description ending "eager to shatter" — nothing else in its own entry
    // agreed with 8. And the only key `typeEffectivenessVs` gives it is `void`,
    // which no bare weapon carries, so at 8 it sat above the force ceiling with
    // one route left and was a door. 7 puts it in the deep sealed-stone band
    // with granite and runestone, which is the band the ceiling exists for.
    density: 2600, hardness: 7, brittleness: 0.85, friction: 0.55, mass: 2.6,
    conductivity: 0.08, heatCapacity: 2.0, corrosionResist: 0.95,
    meltsAt: 1100, meltsInto: 'LAVA',
    ruptureThreshold: 700, shatteredInto: 'GRAVEL', grain: 0.05, colorVariance: 0.03,
    desc: 'Lava quenched by water. Hard, acid-proof, and eager to shatter.',
  });

  rigid('IRON', 'Iron', {
    color: 0x6e6a66, tags: tags('METAL', 'CONDUCTIVE', 'SEALED'),
    density: 7800, hardness: 6, brittleness: 0.1, friction: 0.7, mass: 7.8,
    conductivity: 0.85, heatCapacity: 1.1, electricConductivity: 0.9,
    chargeResistance: 0.06, corrosionResist: 0.1,
    meltsAt: 1538, meltsInto: 'MOLTEN_IRON',
    ruptureThreshold: 1200, shatteredInto: 'RUST_FLAKE', grain: 0.12,
    desc: 'Conducts heat and charge readily. Acid turns it to rust.',
  });

  rigid('RUSTED_IRON', 'Rusted Iron', {
    color: 0x7d502c, tags: tags('METAL', 'CONDUCTIVE', 'POROUS'),
    density: 5200, hardness: 3, brittleness: 0.7, friction: 0.85, mass: 5.2,
    conductivity: 0.5, heatCapacity: 1.0, electricConductivity: 0.4,
    chargeResistance: 0.3, corrosionResist: 0.02,
    ruptureThreshold: 300, shatteredInto: 'RUST_FLAKE', grain: 0.35,
    desc: 'Iron that lost. Brittle, half-conductive, one hit from powder.',
  });

  rigid('STEEL', 'Steel', {
    color: 0x8a929c, tags: tags('METAL', 'CONDUCTIVE', 'SEALED'),
    density: 7900, hardness: 8, brittleness: 0.08, friction: 0.65, mass: 7.9,
    conductivity: 0.8, heatCapacity: 1.15, electricConductivity: 0.85,
    chargeResistance: 0.05, corrosionResist: 0.45,
    meltsAt: 1450, meltsInto: 'MOLTEN_IRON',
    ruptureThreshold: 1600, shatteredInto: 'RUST_FLAKE', grain: 0.08,
    desc: 'Refined iron. Slower to corrode, harder to break, still conducts.',
  });

  rigid('BRASS', 'Brass', {
    color: 0xb08a3e, tags: tags('METAL', 'CONDUCTIVE', 'SEALED'),
    density: 8500, hardness: 5, brittleness: 0.2, friction: 0.6, mass: 8.5,
    conductivity: 0.9, heatCapacity: 1.0, electricConductivity: 0.95,
    chargeResistance: 0.04, corrosionResist: 0.55, resonance: 0.5,
    meltsAt: 930, meltsInto: 'MOLTEN_IRON',
    ruptureThreshold: 900, shatteredInto: 'GRAVEL', grain: 0.1,
    desc: 'Relic alloy. Sings under charge; favoured by clockwork engines.',
  });

  rigid('CERAMIC', 'Ceramic', {
    color: 0xeac4b7, tags: tags('CERAMIC', 'INSULATOR', 'SEALED'),
    density: 2300, hardness: 5, brittleness: 0.9, friction: 0.75, mass: 2.3,
    conductivity: 0.06, heatCapacity: 1.6, corrosionResist: 1.0,
    ruptureThreshold: 320, shatteredInto: 'GRAVEL', grain: 0.06,
    desc: 'Immune to acid, indifferent to heat, and desperate to shatter.',
  });

  rigid('GLASS', 'Glass', {
    color: 0xb0ccda, tags: tags('CERAMIC', 'INSULATOR'),
    density: 2500, hardness: 4, brittleness: 0.98, friction: 0.4, mass: 2.5,
    conductivity: 0.09, heatCapacity: 1.4, corrosionResist: 0.85,
    alpha: 0.35, refraction: 0.4, meltsAt: 900, meltsInto: 'LAVA',
    ruptureThreshold: 150, shatteredInto: 'GRAVEL', grain: 0.02,
    desc: 'Transparent and eager to stop being a wall.',
  });

  rigid('WOOD', 'Wood', {
    color: 0x6b4a2a, tags: tags('FLAMMABLE', 'ORGANIC', 'BIOMASS', 'POROUS', 'INSULATOR'),
    density: 700, hardness: 2, brittleness: 0.45, friction: 0.95, mass: 0.7,
    conductivity: 0.1, heatCapacity: 1.3, ignitionTemp: 250, burnTemp: 700,
    burnDuration: 260, ashesInto: 'ASH', corrosionResist: 0.1,
    ruptureThreshold: 240, shatteredInto: 'ASH', grain: 0.3,
    desc: 'Burns willingly. Acid dissolves it. Still holds up half the world.',
  });

  rigid('PETRIFIED_WOOD', 'Petrified Wood', {
    color: 0x8a6b53, tags: tags('STONE', 'ORGANIC'),
    density: 2300, hardness: 6, brittleness: 0.5, friction: 0.9, mass: 2.3,
    conductivity: 0.12, heatCapacity: 1.9, corrosionResist: 0.6,
    ruptureThreshold: 600, shatteredInto: 'GRAVEL', grain: 0.28,
    desc: 'Living resin that met heat and gave up being alive.',
  });

  rigid('CONCRETE', 'Concrete', {
    color: 0x9d918b, tags: tags('STONE', 'SEALED'),
    density: 2400, hardness: 5, brittleness: 0.35, friction: 0.98, mass: 2.4,
    conductivity: 0.15, heatCapacity: 1.9, corrosionResist: 0.35,
    ruptureThreshold: 620, shatteredInto: 'GRAVEL', grain: 0.14,
    desc: 'Salvage-era stone. Nothing special, everywhere.',
  });

  rigid('PLASTEEL', 'Plasteel', {
    color: 0x4b5765, tags: tags('METAL', 'MACHINE', 'SEALED', 'INSULATOR'),
    density: 3200, hardness: 9, brittleness: 0.05, friction: 0.5, mass: 3.2,
    conductivity: 0.3, heatCapacity: 2.4, electricConductivity: 0.2,
    corrosionResist: 0.9, meltsAt: 2100, meltsInto: 'MOLTEN_IRON',
    ruptureThreshold: 2200, shatteredInto: 'GRAVEL', grain: 0.06,
    desc: 'Far-future composite. Acid-proof, heat-proof, absurdly tough.',
  });

  rigid('BONE', 'Bone', {
    color: 0xd6cdae, tags: tags('ORGANIC', 'UNDEAD', 'BIOMASS', 'POROUS', 'FLAMMABLE'),
    density: 1800, hardness: 3, brittleness: 0.75, friction: 0.8, mass: 1.8,
    conductivity: 0.1, heatCapacity: 1.4, ignitionTemp: 480, burnTemp: 550,
    burnDuration: 180, ashesInto: 'BONE_ASH', corrosionResist: 0.2, sanctity: -0.15,
    ruptureThreshold: 260, shatteredInto: 'BONE_ASH', grain: 0.2,
    desc: 'Brittle when frozen. Radiant matter burns it as if it owed a debt.',
  });

  rigid('CHITIN', 'Chitin', {
    color: 0x492f28, tags: tags('ORGANIC', 'BIOMASS', 'FLAMMABLE', 'INSULATOR'),
    density: 1300, hardness: 4, brittleness: 0.6, friction: 0.7, mass: 1.3,
    conductivity: 0.07, heatCapacity: 1.2, ignitionTemp: 320, burnDuration: 150,
    ashesInto: 'ASH', corrosionResist: 0.4,
    ruptureThreshold: 300, shatteredInto: 'ASH', grain: 0.18,
    desc: 'Armour grown rather than forged. Insulates well; burns eventually.',
  });

  rigid('ICE', 'Ice', {
    color: 0xa6d8ec, tags: tags('CRYO', 'SLICK', 'INSULATOR'),
    density: 917, hardness: 2, brittleness: 0.9, friction: 0.12, mass: 0.92,
    temp: -12, conductivity: 0.4, heatCapacity: 1.0,
    meltsAt: 0, meltsInto: 'WATER',
    ruptureThreshold: 120, shatteredInto: 'SNOW', alpha: 0.82, refraction: 0.25,
    grain: 0.12,
    desc: 'Slick underfoot. Wall-slides on it are fast and unforgiving.',
  });

  rigid('PERMAFROST', 'Permafrost', {
    color: 0x7798ae, tags: tags('CRYO', 'STONE', 'SEALED'),
    density: 1900, hardness: 5, brittleness: 0.65, friction: 0.5, mass: 1.9,
    temp: -30, conductivity: 0.25, heatCapacity: 2.1,
    meltsAt: 4, meltsInto: 'MUD',
    ruptureThreshold: 420, shatteredInto: 'GRAVEL', grain: 0.2,
    desc: 'Frozen ground. Thaw it and the floor becomes mud.',
  });

  rigid('CRYSTAL', 'Resonant Crystal', {
    color: 0xc79ce8, tags: tags('CRYSTAL', 'CERAMIC', 'CONDUCTIVE'),
    density: 2650, hardness: 6, brittleness: 0.8, friction: 0.45, mass: 2.65,
    conductivity: 0.35, heatCapacity: 1.5, electricConductivity: 0.55,
    chargeResistance: 0.02, resonance: 1.0, emissive: 0.55,
    corrosionResist: 0.75, alpha: 0.75, refraction: 0.6,
    ruptureThreshold: 280, shatteredInto: 'ALCHEMICAL_DUST', grain: 0.08,
    // 0.25 -> 0.55 crosses LIGHT_SOURCE_MIN, which is issue #31's cheapest
    // sentence: a material described as "focuses beams" was rendering as a
    // grey rock, and it is the authored accent of the two biomes that had no
    // other light, and at 0.4 those two bands still measured below the
    // legibility floor once a set piece opened a hall in them. Every other
    // emissive value in the roster is untouched.
    desc: 'Stores charge, focuses beams, and remembers what passed through it.',
  });

  rigid('VOIDGLASS', 'Voidglass', {
    color: 0x14101e, tags: tags('VOID', 'CERAMIC', 'INSULATOR', 'SEALED'),
    density: 2900, hardness: 7, brittleness: 0.7, friction: 0.3, mass: 2.9,
    conductivity: 0.02, heatCapacity: 3.0, corrosionResist: 0.9,
    sanctity: -0.6, temporalStability: 0.7, alpha: 0.95,
    ruptureThreshold: 500, shatteredInto: 'VOID_MATTER', grain: 0.04, colorVariance: 0.02,
    desc: 'Absorbs light entirely. Rooms walled in it are genuinely dark.',
  });

  rigid('ADAMANT_SEAL', 'Adamant Seal', {
    color: 0x364a68, tags: tags('SEALED', 'INSULATOR', 'SACRED'),
    density: 9000, hardness: 10, brittleness: 0, friction: 0.8, mass: 9,
    conductivity: 0.01, heatCapacity: 8, corrosionResist: 1,
    indestructible: true, resonance: 0.8, emissive: 0.08,
    ruptureThreshold: 99999, grain: 0.05,
    desc: 'A door that is not a wall. Only the matching key opens it.',
  });

  rigid('CIRCUIT_PLATE', 'Circuit Plate', {
    color: 0x2f6b52, tags: tags('MACHINE', 'CONDUCTIVE', 'METAL'),
    density: 2100, hardness: 3, brittleness: 0.6, friction: 0.6, mass: 2.1,
    conductivity: 0.6, heatCapacity: 1.0, electricConductivity: 0.98,
    chargeResistance: 0.03, shortCircuits: true, corrosionResist: 0.25,
    emissive: 0.12, ruptureThreshold: 260, shatteredInto: 'NANITE_DUST', grain: 0.25,
    desc: 'Carries charge beautifully and dies of it just as readily.',
  });

  rigid('RUNESTONE', 'Runestone', {
    color: 0x6b6a86, tags: tags('STONE', 'SACRED', 'CRYSTAL'),
    density: 2700, hardness: 7, brittleness: 0.3, friction: 0.9, mass: 2.7,
    conductivity: 0.12, heatCapacity: 2.4, resonance: 0.9, sanctity: 0.5,
    emissive: 0.18, corrosionResist: 0.85, temporalStability: 1.4,
    ruptureThreshold: 850, shatteredInto: 'GRAVEL', grain: 0.2,
    desc: 'Anchors local reality. Blink and phase effects fail against it.',
  });

  rigid('CORPSE_STONE', 'Corpse Stone', {
    color: 0x59434a, tags: tags('STONE', 'UNDEAD', 'CURSED', 'BIOMASS'),
    density: 2500, hardness: 5, brittleness: 0.45, friction: 0.88, mass: 2.5,
    conductivity: 0.1, heatCapacity: 2.0, sanctity: -0.5, bioActivity: 0.15,
    corrosionResist: 0.4, ruptureThreshold: 480, shatteredInto: 'GRAVE_SOIL',
    grain: 0.26,
    desc: 'Stone that absorbed too many burials. It is faintly aware of you.',
  });

  // ---------------------------------------------- RIGID: the biome lights
  //
  // Issue #31. `M.lightSource` is derived from `emissive >= LIGHT_SOURCE_MIN`,
  // and before these four the only materials over that line were fire, lava,
  // molten iron and radiant matter — none of which worldgen places outside two
  // biomes. So ten of the twelve biomes had **no light source anywhere in
  // them** and rendered every cell at ambient, which is the whole of #31.
  //
  // Three authoring rules, and they are the reason these are their own block:
  //
  //  1. **Luminescence, not combustion.** Every one spawns at room temperature
  //     with no `ignitionTemp`. A glow material that is also hot would mean
  //     worldgen laying thousands of ignition sources through every biome, and
  //     the biome already has its own hazard for that.
  //  2. **Rigid and low hardness.** They are placed into the exposed face of a
  //     wall (`worldgen.ts` `glowVeins`), so they must not fall, and they must
  //     be worth digging out — breaking the light is a real trade in a game
  //     where the dark is a mechanic.
  //  3. **They shatter into the biome's own rubble.** Mining a vein costs you
  //     the light and gives you the biome's ordinary powder, which is the
  //     honest exchange rate.

  rigid('GLOWCAP', 'Glowcap', {
    color: 0x9ffbd8, tags: tags('FUNGAL', 'ORGANIC', 'BIOMASS', 'LIVING'),
    density: 900, hardness: 2, brittleness: 0.75, friction: 0.9, mass: 0.9,
    conductivity: 0.18, heatCapacity: 1.6, emissive: 0.85, bioActivity: 0.6,
    corrosionResist: 0.05, alpha: 0.95,
    ruptureThreshold: 180, shatteredInto: 'SPORE_DUST', grain: 0.3,
    colorVariance: 0.16,
    desc: 'Cold light growing where the damp is. Break it and the room goes dark.',
  });

  rigid('EMBER_VEIN', 'Ember Vein', {
    color: 0xc41a3c, tags: tags('STONE', 'INSULATOR', 'SEALED'),
    density: 2400, hardness: 3, brittleness: 0.6, friction: 0.9, mass: 2.4,
    conductivity: 0.16, heatCapacity: 2.0, emissive: 0.9, corrosionResist: 0.6,
    ruptureThreshold: 300, shatteredInto: 'EMBER_GRIT', grain: 0.24,
    colorVariance: 0.14,
    desc: 'A seam that kept the furnace light without the heat. Break it for embers.',
  });

  rigid('SIGNAL_BLOOM', 'Signal Bloom', {
    color: 0x30f0d0, tags: tags('MACHINE', 'CONDUCTIVE', 'NANITE'),
    density: 1600, hardness: 2, brittleness: 0.7, friction: 0.65, mass: 1.6,
    conductivity: 0.4, heatCapacity: 1.3, electricConductivity: 0.7,
    chargeResistance: 0.05, shortCircuits: true, emissive: 0.85,
    corrosionResist: 0.2, alpha: 0.92,
    ruptureThreshold: 200, shatteredInto: 'NANITE_DUST', grain: 0.22,
    colorVariance: 0.12,
    desc: 'A colony still broadcasting. Charge crossing it makes it brighter, then dark.',
  });

  rigid('SAINTLIGHT', 'Saintlight', {
    color: 0x4fa8ff, tags: tags('STONE', 'SACRED', 'CERAMIC', 'SEALED'),
    density: 2300, hardness: 4, brittleness: 0.7, friction: 0.75, mass: 2.3,
    conductivity: 0.1, heatCapacity: 1.8, emissive: 0.95, sanctity: 1,
    resonance: 0.6, corrosionResist: 0.9, alpha: 0.9,
    ruptureThreshold: 320, shatteredInto: 'SALT', grain: 0.12,
    colorVariance: 0.1,
    // The Cathedral's slot could not be RADIANT_MATTER, which is what it looked
    // like it should be: that is a PLASMA with `dispersion: 2` and `temp: 200`,
    // so a vein of it flows out of the wall it was placed in on the first tick
    // and heats what it lands on. An ambient light has to still be there when
    // the player walks in.
    desc: 'Cold blue light set in the salt. The wards read by it.',
  });

  rigid('CHOIRLIGHT', 'Choirlight', {
    color: 0x8f7bff, tags: tags('VOID', 'CRYSTAL', 'INSULATOR'),
    density: 2200, hardness: 3, brittleness: 0.8, friction: 0.4, mass: 2.2,
    conductivity: 0.04, heatCapacity: 3.0, emissive: 1.0, sanctity: -0.4,
    temporalStability: 0.8, resonance: 0.85, corrosionResist: 0.85, alpha: 0.9,
    ruptureThreshold: 240, shatteredInto: 'GRAVEL', grain: 0.16,
    colorVariance: 0.1,
    desc: 'The only thing down here that gives light back. It is not friendly about it.',
  });

  // ------------------------------------------------------------ LOOSE

  loose('SAND', 'Sand', {
    color: 0xc9a86a, tags: tags('POROUS'),
    density: 1600, hardness: 1, brittleness: 1, friction: 0.85, mass: 1.6,
    dispersion: 1, conductivity: 0.1, heatCapacity: 1.2,
    meltsAt: 1500, meltsInto: 'LAVA', corrosionResist: 0.5, grain: 0.35,
    colorVariance: 0.14,
    desc: 'Flows, piles, and buries. Burrowing bodies move through it freely.',
  });

  loose('GRAVEL', 'Gravel', {
    color: 0x787879, tags: tags('STONE'),
    density: 1900, hardness: 2, brittleness: 0.9, friction: 0.95, mass: 1.9,
    dispersion: 0, conductivity: 0.12, heatCapacity: 1.6, corrosionResist: 0.6,
    grain: 0.4, colorVariance: 0.18,
    desc: 'What broken stone becomes. Piles steeply, trips the unwary.',
  });

  loose('ASH', 'Ash', {
    color: 0xc9bdb2, tags: tags('POROUS'),
    density: 500, hardness: 0.5, brittleness: 1, friction: 0.7, mass: 0.5,
    dispersion: 1, temp: 90, conductivity: 0.06, heatCapacity: 0.9,
    corrosionResist: 0.3, grain: 0.3, colorVariance: 0.16,
    desc: 'The end state of nearly everything organic.',
  });

  loose('BONE_ASH', 'Bone Ash', {
    color: 0xbfb49a, tags: tags('POROUS', 'UNDEAD'),
    density: 620, hardness: 0.5, brittleness: 1, friction: 0.72, mass: 0.62,
    dispersion: 1, conductivity: 0.05, heatCapacity: 0.9, sanctity: -0.2,
    grain: 0.3, colorVariance: 0.12,
    desc: 'Fine, pale, and useful to anyone who works with the dead.',
  });

  loose('SNOW', 'Snow', {
    color: 0xeaf2f7, tags: tags('CRYO', 'POROUS', 'SLICK'),
    density: 300, hardness: 0.3, brittleness: 1, friction: 0.45, mass: 0.3,
    dispersion: 1, temp: -8, conductivity: 0.15, heatCapacity: 0.8,
    meltsAt: 1, meltsInto: 'WATER', grain: 0.15, colorVariance: 0.05,
    desc: 'Melts into water, which puts out fires, which then conducts. Chain.',
  });

  loose('SOOT', 'Soot', {
    color: 0x222e24, tags: tags('FLAMMABLE', 'POROUS'),
    density: 400, hardness: 0.3, brittleness: 1, friction: 0.6, mass: 0.4,
    dispersion: 2, ignitionTemp: 420, burnDuration: 40, ashesInto: 'ASH',
    conductivity: 0.05, heatCapacity: 0.7, grain: 0.34,
    desc: 'Unburnt carbon. Settles on everything and remembers the fire.',
  });

  loose('COAL_DUST', 'Coal Dust', {
    color: 0x231a21, tags: tags('FLAMMABLE', 'ACCELERANT', 'EXPLOSIVE', 'VOLATILE'),
    density: 700, hardness: 0.4, brittleness: 1, friction: 0.65, mass: 0.7,
    dispersion: 2, ignitionTemp: 180, burnTemp: 900, burnDuration: 90,
    ashesInto: 'ASH', reactivity: 1.8, conductivity: 0.08, heatCapacity: 0.8,
    grain: 0.3,
    desc: 'Suspended in air it does not burn. It detonates.',
  });

  loose('ALCHEMICAL_DUST', 'Alchemical Dust', {
    color: 0xc46be0, tags: tags('VOLATILE', 'SOLUBLE', 'CRYSTAL'),
    density: 900, hardness: 0.4, brittleness: 1, friction: 0.6, mass: 0.9,
    dispersion: 1, reactivity: 2.4, resonance: 0.6, emissive: 0.15,
    conductivity: 0.2, heatCapacity: 1.0, ignitionTemp: 300, burnTemp: 1100,
    burnDuration: 30, ashesInto: 'ASH', grain: 0.28, colorVariance: 0.2,
    desc: 'Reacts with almost anything. That is both the appeal and the problem.',
  });

  loose('RUST_FLAKE', 'Rust Flake', {
    color: 0x8c4029, tags: tags('METAL', 'POROUS'),
    density: 1100, hardness: 0.5, brittleness: 1, friction: 0.8, mass: 1.1,
    dispersion: 0, conductivity: 0.2, heatCapacity: 1.0, electricConductivity: 0.1,
    corrosionResist: 0.05, grain: 0.36, colorVariance: 0.2,
    desc: 'Iron, finished. Barely conducts, dissolves on contact with acid.',
  });

  loose('SALT', 'Salt', {
    color: 0xe8e4dc, tags: tags('SOLUBLE', 'SACRED'),
    density: 2100, hardness: 1, brittleness: 1, friction: 0.8, mass: 2.1,
    dispersion: 1, conductivity: 0.1, heatCapacity: 1.1, sanctity: 0.3,
    corrosionResist: 0.7, grain: 0.22, colorVariance: 0.08,
    desc: 'Dissolves into brine. Wards work poorly without it.',
  });

  loose('SPORE_DUST', 'Spore Dust', {
    color: 0x879a45, tags: tags('FUNGAL', 'ORGANIC', 'FLAMMABLE', 'EXPLOSIVE', 'BIOMASS'),
    density: 350, hardness: 0.2, brittleness: 1, friction: 0.55, mass: 0.35,
    dispersion: 2, ignitionTemp: 200, burnTemp: 800, burnDuration: 25,
    ashesInto: 'ASH', bioActivity: 0.5, reactivity: 1.6, toxicity: 0.1,
    conductivity: 0.05, heatCapacity: 0.8, grain: 0.3,
    desc: 'Airborne fungal matter. A torch in a spore cloud is a decision.',
  });

  loose('NANITE_DUST', 'Nanite Dust', {
    color: 0x728c96, tags: tags('NANITE', 'MACHINE', 'CONDUCTIVE'),
    density: 1400, hardness: 0.6, brittleness: 1, friction: 0.6, mass: 1.4,
    dispersion: 1, conductivity: 0.4, heatCapacity: 1.0, electricConductivity: 0.5,
    shortCircuits: true, bioActivity: 0.3, emissive: 0.06, grain: 0.26,
    desc: 'Dormant machines. They wake near corpses and circuits.',
  });

  loose('GRAVE_SOIL', 'Grave Soil', {
    color: 0x3b4131, tags: tags('ORGANIC', 'BIOMASS', 'UNDEAD', 'POROUS', 'CURSED'),
    density: 1500, hardness: 1, brittleness: 1, friction: 0.9, mass: 1.5,
    dispersion: 0, conductivity: 0.1, heatCapacity: 1.4, bioActivity: 0.35,
    sanctity: -0.4, grain: 0.32, colorVariance: 0.16,
    desc: 'Soil that has held too many. Add blood and something stands up.',
  });

  loose('EMBER_GRIT', 'Ember Grit', {
    color: 0xa8482a, tags: tags('PYRO', 'FLAMMABLE'),
    density: 1300, hardness: 0.8, brittleness: 1, friction: 0.8, mass: 1.3,
    dispersion: 1, temp: 340, emissive: 0.4, ignitionTemp: 300, burnTemp: 650,
    burnDuration: 400, ashesInto: 'ASH', conductivity: 0.2, heatCapacity: 1.0,
    grain: 0.35, colorVariance: 0.24,
    desc: 'Ground that never finished burning. Walking on it is a commitment.',
  });

  // ----------------------------------------------------------- LIQUID

  liquid('WATER', 'Water', {
    color: 0x3a7ec4, tags: tags('CONDUCTIVE', 'SOLUBLE', 'SLICK'),
    density: 1000, viscosity: 0.02, dispersion: 5, hardness: 0, friction: 0.35,
    mass: 1, conductivity: 0.6, heatCapacity: 4.2, electricConductivity: 0.45,
    chargeResistance: 0.12, freezesAt: 0, freezesInto: 'ICE',
    boilsAt: 100, boilsInto: 'STEAM', alpha: 0.55, refraction: 0.3, grain: 0.05,
    desc: 'Extinguishes, conducts, floods, freezes. The most useful thing there is.',
  });

  liquid('SALTWATER', 'Saltwater', {
    color: 0x2f6d9e, tags: tags('CONDUCTIVE', 'SOLUBLE', 'CORROSIVE', 'SLICK'),
    density: 1030, viscosity: 0.03, dispersion: 5, hardness: 0, friction: 0.35,
    mass: 1.03, conductivity: 0.6, heatCapacity: 4.0, electricConductivity: 0.8,
    chargeResistance: 0.06, corrosiveness: 0.12, freezesAt: -2, freezesInto: 'ICE',
    boilsAt: 103, boilsInto: 'STEAM', alpha: 0.58, refraction: 0.3,
    desc: 'Conducts nearly twice as well as fresh water, and rusts what it touches.',
  });

  liquid('OIL', 'Oil', {
    color: 0x4c370b, tags: tags('FLAMMABLE', 'ACCELERANT', 'SLICK', 'INSULATOR'),
    density: 880, viscosity: 0.18, dispersion: 3, hardness: 0, friction: 0.1,
    mass: 0.88, conductivity: 0.14, heatCapacity: 1.9, ignitionTemp: 210,
    burnTemp: 950, burnDuration: 300, ashesInto: 'SOOT',
    electricConductivity: 0, alpha: 0.88, grain: 0.08,
    desc: 'Floats on water and turns its surface into a fuse.',
  });

  liquid('CRUDE_TAR', 'Crude Tar', {
    color: 0x3b2a12, tags: tags('FLAMMABLE', 'ACCELERANT', 'STICKY', 'INSULATOR'),
    density: 1100, viscosity: 0.65, dispersion: 1, hardness: 0, friction: 0.95,
    mass: 1.1, conductivity: 0.1, heatCapacity: 2.0, ignitionTemp: 280,
    burnTemp: 800, burnDuration: 900, ashesInto: 'SOOT',
    freezesAt: 20, freezesInto: 'TAR', alpha: 0.96,
    desc: 'Barely flows. Burns for an extremely long time.',
  });

  liquid('ACID', 'Acid', {
    color: 0x8ede2a, tags: tags('CORROSIVE', 'TOXIC', 'VOLATILE'),
    density: 1200, viscosity: 0.05, dispersion: 4, hardness: 0, friction: 0.4,
    mass: 1.2, conductivity: 0.5, heatCapacity: 3.2, electricConductivity: 0.6,
    corrosiveness: 1.0, toxicity: 0.7, reactivity: 1.6,
    boilsAt: 120, boilsInto: 'TOXIC_SMOKE', emissive: 0.12,
    alpha: 0.7, refraction: 0.25, grain: 0.06,
    desc: 'Eats organics fast, metal slowly, soft stone eventually — ceramic never.',
  });

  liquid('BLOOD', 'Blood', {
    color: 0x8f1f22, tags: tags('BLOOD', 'ORGANIC', 'BIOMASS', 'CONDUCTIVE'),
    density: 1060, viscosity: 0.12, dispersion: 3, hardness: 0, friction: 0.5,
    mass: 1.06, temp: 37, conductivity: 0.55, heatCapacity: 3.9,
    electricConductivity: 0.4, bioActivity: 0.2, boilsAt: 105, boilsInto: 'STEAM',
    freezesAt: -2, freezesInto: 'ICE', alpha: 0.9, grain: 0.07,
    desc: 'Ritual fuel. Feeds curses, accelerates fungus, attracts what hunts.',
  });

  liquid('CURSED_BLOOD', 'Cursed Blood', {
    color: 0x4a0f2e, tags: tags('BLOOD', 'CURSED', 'ORGANIC', 'TOXIC', 'CONDUCTIVE'),
    density: 1120, viscosity: 0.2, dispersion: 2, hardness: 0, friction: 0.55,
    mass: 1.12, temp: 12, conductivity: 0.4, heatCapacity: 3.6, toxicity: 0.5,
    sanctity: -0.9, bioActivity: 0.4, electricConductivity: 0.35,
    emissive: 0.1, alpha: 0.94,
    desc: 'Blood that made a bargain. Powers curse modules; radiant matter hates it.',
  });

  liquid('LAVA', 'Lava', {
    color: 0xe0561a, tags: tags('PYRO', 'STONE', 'VOLATILE'),
    density: 2800, viscosity: 0.55, dispersion: 1, hardness: 0, friction: 0.7,
    mass: 2.8, temp: 1150, conductivity: 0.5, heatCapacity: 2.4,
    burnTemp: 1150, emissive: 1.0, freezesAt: 700, freezesInto: 'OBSIDIAN',
    alpha: 1, grain: 0.16, colorVariance: 0.2,
    desc: 'Ignites everything nearby. Water turns it to obsidian, violently.',
  });

  liquid('MOLTEN_IRON', 'Molten Iron', {
    color: 0xffb057, tags: tags('PYRO', 'METAL', 'CONDUCTIVE'),
    density: 6900, viscosity: 0.25, dispersion: 2, hardness: 0, friction: 0.5,
    mass: 6.9, temp: 1600, conductivity: 0.9, heatCapacity: 1.2,
    electricConductivity: 0.8, burnTemp: 1600, emissive: 1.0,
    freezesAt: 1400, freezesInto: 'IRON', grain: 0.1,
    desc: 'Sinks through nearly everything on its way down, burning as it goes.',
  });

  liquid('MERCURY', 'Mercury', {
    color: 0xc4c8cc, tags: tags('METAL', 'CONDUCTIVE', 'TOXIC', 'SLICK'),
    density: 13500, viscosity: 0.08, dispersion: 4, hardness: 0, friction: 0.15,
    mass: 13.5, conductivity: 0.8, heatCapacity: 1.4, electricConductivity: 1.0,
    chargeResistance: 0.0, toxicity: 0.6, freezesAt: -39, freezesInto: 'IRON',
    boilsAt: 357, boilsInto: 'TOXIC_SMOKE', emissive: 0.05, grain: 0.04,
    desc: 'Superconducts — charge crosses it without attenuating at all.',
  });

  liquid('REACTOR_COOLANT', 'Reactor Coolant', {
    color: 0x36c9b8, tags: tags('CRYO', 'MACHINE', 'CONDUCTIVE', 'VOLATILE', 'TOXIC'),
    density: 1150, viscosity: 0.06, dispersion: 4, hardness: 0, friction: 0.3,
    mass: 1.15, temp: -20, conductivity: 0.75, heatCapacity: 5.5,
    electricConductivity: 0.5, toxicity: 0.3, reactivity: 1.5,
    boilsAt: 60, boilsInto: 'FLASH_VAPOR', emissive: 0.2, alpha: 0.7,
    refraction: 0.2,
    desc: 'Enormous heat capacity. Meets plasma and becomes a pressure event.',
  });

  liquid('MACHINE_OIL', 'Machine Oil', {
    color: 0x4d4632, tags: tags('FLAMMABLE', 'ACCELERANT', 'MACHINE', 'SLICK', 'INSULATOR'),
    density: 900, viscosity: 0.22, dispersion: 3, hardness: 0, friction: 0.12,
    mass: 0.9, conductivity: 0.15, heatCapacity: 2.0, ignitionTemp: 240,
    burnTemp: 880, burnDuration: 340, ashesInto: 'SOOT', alpha: 0.9,
    desc: 'Every machine leaks it. Every overheating machine regrets that.',
  });

  liquid('ALCOHOL', 'Spirits', {
    color: 0xdadfa4, tags: tags('FLAMMABLE', 'ACCELERANT', 'SOLUBLE', 'VOLATILE'),
    density: 790, viscosity: 0.01, dispersion: 5, hardness: 0, friction: 0.3,
    mass: 0.79, conductivity: 0.3, heatCapacity: 2.4, ignitionTemp: 90,
    burnTemp: 900, burnDuration: 60, ashesInto: 'VOID',
    boilsAt: 78, boilsInto: 'FLASH_VAPOR', alpha: 0.4, refraction: 0.2,
    desc: 'Ignites at almost nothing, burns nearly invisibly, leaves no residue.',
  });

  liquid('BRINE', 'Brine', {
    color: 0x5f8fa8, tags: tags('CONDUCTIVE', 'CORROSIVE', 'SOLUBLE'),
    density: 1180, viscosity: 0.04, dispersion: 5, hardness: 0, friction: 0.35,
    mass: 1.18, conductivity: 0.6, heatCapacity: 3.8, electricConductivity: 0.9,
    chargeResistance: 0.04, corrosiveness: 0.25, freezesAt: -10, freezesInto: 'ICE',
    boilsAt: 108, boilsInto: 'STEAM', alpha: 0.6,
    desc: 'Salt-saturated water. The best conductor you can make by accident.',
  });

  liquid('PLAGUE_SLURRY', 'Plague Slurry', {
    color: 0x6c702e, tags: tags('TOXIC', 'ORGANIC', 'BIOMASS', 'CURSED', 'STICKY'),
    density: 1250, viscosity: 0.5, dispersion: 1, hardness: 0, friction: 0.8,
    mass: 1.25, conductivity: 0.3, heatCapacity: 3.0, toxicity: 1.0,
    bioActivity: 0.6, sanctity: -0.5, ignitionTemp: 400, burnDuration: 80,
    ashesInto: 'ASH', alpha: 0.95, grain: 0.12,
    desc: 'Contagious sludge. Sanctified mist neutralises it into plain salt.',
  });

  liquid('LIQUID_VOID', 'Liquid Void', {
    color: 0x2d112f, tags: tags('VOID', 'VOLATILE', 'INSULATOR'),
    density: 1400, viscosity: 0.3, dispersion: 2, hardness: 0, friction: 0.2,
    mass: 1.4, temp: -100, conductivity: 0.01, heatCapacity: 6,
    sanctity: -1, temporalStability: 0.4, reactivity: 2.0, emissive: 0.0,
    alpha: 1, grain: 0.1, colorVariance: 0.03,
    desc: 'Erases what it touches. Meets radiant ichor and both simply stop.',
  });

  liquid('RADIANT_ICHOR', 'Radiant Ichor', {
    color: 0xffe9a8, tags: tags('RADIANT', 'SACRED', 'VOLATILE'),
    density: 1050, viscosity: 0.1, dispersion: 4, hardness: 0, friction: 0.3,
    mass: 1.05, temp: 60, conductivity: 0.5, heatCapacity: 3.0,
    sanctity: 1, emissive: 0.9, reactivity: 1.8, resonance: 0.7,
    alpha: 0.75, refraction: 0.2,
    desc: 'Burns the undead, purifies corruption, and annihilates void on contact.',
  });

  liquid('CHRONO_FLUID', 'Chrono Fluid', {
    color: 0x8edeec, tags: tags('TEMPORAL', 'VOLATILE', 'CRYSTAL'),
    density: 990, viscosity: 0.15, dispersion: 3, hardness: 0, friction: 0.3,
    mass: 0.99, conductivity: 0.4, heatCapacity: 3.4, temporalStability: 0.25,
    resonance: 0.8, emissive: 0.35, reactivity: 1.7, alpha: 0.65, refraction: 0.45,
    desc: 'Objects that pass through it arrive twice, a moment apart.',
  });

  // -------------------------------------------------------------- GAS
  //
  // `burnDuration: 0` on every gas that is not itself a fuel is load-bearing,
  // not tidying: `world.set` seeds `aux` from `burnDuration` and cellsim reads
  // that same `aux` as the lifetime counter, so a default 120 silently docked
  // 120 ticks off every declared lifetime — flash vapour lived 60 ticks of its
  // 180, and lightning's three ticks were zero. METHANE and SPORE_CLOUD keep a
  // real burnDuration because they genuinely are fuel; thermal re-seeds `aux`
  // for them at ignition.

  gas('STEAM', 'Steam', {
    lifetime: 240, burnDuration: 0,
    color: 0xc8dad8, tags: tags('PYRO'),
    density: 0.6, viscosity: 0, dispersion: 4, hardness: 0, mass: 0.02,
    temp: 110, conductivity: 0.3, heatCapacity: 2.0, alpha: 0.3,
    condensesAt: 95, condensesInto: 'WATER', grain: 0.4,
    desc: 'Hot, obscuring, and harmless once it cools back into water.',
  });

  gas('SMOKE', 'Smoke', {
    lifetime: 420, burnDuration: 0,
    color: 0x443343, tags: tags('POROUS'),
    density: 0.5, viscosity: 0, dispersion: 4, hardness: 0, mass: 0.02,
    temp: 200, conductivity: 0.1, heatCapacity: 1.0, alpha: 0.45,
    grain: 0.45,
    desc: 'Blocks sight. Fires make far more of it than players expect.',
  });

  gas('TOXIC_SMOKE', 'Toxic Smoke', {
    lifetime: 600, burnDuration: 0,
    color: 0x73803c, tags: tags('TOXIC', 'POROUS'),
    density: 0.9, viscosity: 0, dispersion: 3, hardness: 0, mass: 0.03,
    temp: 120, conductivity: 0.1, heatCapacity: 1.0, toxicity: 0.5,
    alpha: 0.5, grain: 0.42,
    desc: 'Denser than air, so it pools in low ground and stays there.',
  });

  gas('CHLORINE', 'Chlorine', {
    lifetime: 1200, burnDuration: 0,
    color: 0xa8d84a, tags: tags('TOXIC', 'CORROSIVE', 'VOLATILE'),
    density: 3.2, viscosity: 0, dispersion: 3, hardness: 0, mass: 0.05,
    conductivity: 0.1, heatCapacity: 0.9, toxicity: 0.9, corrosiveness: 0.3,
    reactivity: 1.5, alpha: 0.4, grain: 0.35,
    desc: 'Heavier than air. Sinks into trenches and waits at ankle height.',
  });

  gas('METHANE', 'Methane', {
    lifetime: 900,
    color: 0x788c65, tags: tags('FLAMMABLE', 'ACCELERANT', 'EXPLOSIVE', 'VOLATILE'),
    density: 0.35, viscosity: 0, dispersion: 5, hardness: 0, mass: 0.01,
    ignitionTemp: 140, burnTemp: 1000, burnDuration: 8, conductivity: 0.1,
    heatCapacity: 0.8, reactivity: 2.0, alpha: 0.15, grain: 0.3,
    desc: 'Pools at ceilings. A sealed pocket of it is a room-sized bomb.',
  });

  gas('SPORE_CLOUD', 'Spore Cloud', {
    lifetime: 900,
    color: 0x93a855, tags: tags('FUNGAL', 'ORGANIC', 'FLAMMABLE', 'EXPLOSIVE', 'TOXIC'),
    density: 0.8, viscosity: 0, dispersion: 3, hardness: 0, mass: 0.03,
    ignitionTemp: 180, burnTemp: 850, burnDuration: 10, bioActivity: 0.7,
    toxicity: 0.25, conductivity: 0.08, heatCapacity: 0.9, reactivity: 1.7,
    alpha: 0.42, grain: 0.4,
    desc: 'Seeds fungal growth wherever it settles. Detonates on contact with flame.',
  });

  gas('VOID_MIST', 'Void Mist', {
    lifetime: 1500, burnDuration: 0,
    color: 0x191a3d, tags: tags('VOID', 'INSULATOR'),
    density: 1.6, viscosity: 0, dispersion: 2, hardness: 0, mass: 0.04,
    temp: -60, conductivity: 0.01, heatCapacity: 4, sanctity: -0.7,
    temporalStability: 0.6, alpha: 0.55, grain: 0.3,
    desc: 'Mutes light and sound. Beams entering it do not come out.',
  });

  gas('SANCTIFIED_MIST', 'Sanctified Mist', {
    lifetime: 600, burnDuration: 0,
    color: 0xf2e6c0, tags: tags('RADIANT', 'SACRED'),
    density: 0.7, viscosity: 0, dispersion: 4, hardness: 0, mass: 0.02,
    conductivity: 0.2, heatCapacity: 1.5, sanctity: 0.8, emissive: 0.5,
    alpha: 0.35, grain: 0.3,
    desc: 'Purifies plague and corruption. Undead will not willingly enter it.',
  });

  gas('NANITE_FOG', 'Nanite Fog', {
    lifetime: 1200, burnDuration: 0,
    color: 0x9391b4, tags: tags('NANITE', 'MACHINE', 'CONDUCTIVE'),
    density: 1.2, viscosity: 0, dispersion: 3, hardness: 0, mass: 0.03,
    conductivity: 0.4, heatCapacity: 1.2, electricConductivity: 0.4,
    bioActivity: 0.5, emissive: 0.15, alpha: 0.4, grain: 0.32,
    desc: 'Airborne machines looking for something to repair or consume.',
  });

  gas('FLASH_VAPOR', 'Flash Vapor', {
    lifetime: 180, burnDuration: 0,
    color: 0xcdf2e6, tags: tags('VOLATILE', 'EXPLOSIVE', 'CRYO'),
    density: 0.4, viscosity: 0, dispersion: 6, hardness: 0, mass: 0.015,
    temp: 80, conductivity: 0.35, heatCapacity: 1.6, reactivity: 2.2,
    ruptureThreshold: 90, condensesAt: 40, condensesInto: 'REACTOR_COOLANT',
    emissive: 0.2, alpha: 0.28, grain: 0.36,
    desc: 'Coolant that boiled. Expands hard enough to blow out sealed rooms.',
  });

  // ----------------------------------------------------------- SLUDGE

  sludge('MUD', 'Mud', {
    color: 0x4f4221, tags: tags('POROUS', 'STICKY', 'ORGANIC'),
    density: 1700, viscosity: 0.55, dispersion: 1, hardness: 0.5, friction: 0.95,
    mass: 1.7, conductivity: 0.3, heatCapacity: 2.6, freezesAt: -4,
    freezesInto: 'PERMAFROST', alpha: 1, grain: 0.24,
    desc: 'Slows you down and holds you there. Conducts better than dry ground.',
  });

  sludge('TAR', 'Cold Tar', {
    color: 0x18150a, tags: tags('FLAMMABLE', 'ACCELERANT', 'STICKY', 'INSULATOR'),
    density: 1200, viscosity: 0.9, dispersion: 0, hardness: 1, friction: 1.0,
    mass: 1.2, conductivity: 0.08, heatCapacity: 2.0, ignitionTemp: 300,
    burnTemp: 780, burnDuration: 1200, ashesInto: 'SOOT',
    meltsAt: 45, meltsInto: 'CRUDE_TAR', alpha: 1,
    desc: 'Nearly immobile. Once lit, it is a hazard zone for a very long time.',
  });

  sludge('FLESH', 'Flesh', {
    color: 0xa8524e, tags: tags('ORGANIC', 'BIOMASS', 'FLAMMABLE', 'LIVING'),
    density: 1050, viscosity: 0.7, dispersion: 0, hardness: 1.5, friction: 0.85,
    mass: 1.05, temp: 36, conductivity: 0.35, heatCapacity: 3.4,
    ignitionTemp: 340, burnTemp: 620, burnDuration: 200, ashesInto: 'ASH',
    bioActivity: 0.3, corrosionResist: 0.05, alpha: 1, grain: 0.18,
    desc: 'Acid consumes it in seconds. Fungus prefers it to anything else.',
  });

  sludge('ROTTEN_FLESH', 'Rotten Flesh', {
    color: 0x676b46, tags: tags('ORGANIC', 'BIOMASS', 'UNDEAD', 'TOXIC', 'FLAMMABLE'),
    density: 980, viscosity: 0.6, dispersion: 0, hardness: 1, friction: 0.85,
    mass: 0.98, conductivity: 0.3, heatCapacity: 3.2, ignitionTemp: 300,
    burnDuration: 160, ashesInto: 'ASH', toxicity: 0.4, bioActivity: 0.5,
    sanctity: -0.6, corrosionResist: 0.05, grain: 0.22,
    desc: 'Holy fire burns it without needing fuel and does not stop.',
  });

  sludge('FUNGAL_MAT', 'Fungal Mat', {
    color: 0x5a7b3a, tags: tags('FUNGAL', 'ORGANIC', 'BIOMASS', 'FLAMMABLE', 'LIVING', 'STICKY'),
    density: 600, viscosity: 0.4, dispersion: 0, hardness: 1, friction: 1.0,
    mass: 0.6, conductivity: 0.15, heatCapacity: 1.8, ignitionTemp: 220,
    burnTemp: 700, burnDuration: 120, ashesInto: 'ASH', bioActivity: 0.9,
    toxicity: 0.15, emissive: 0.06, grain: 0.28, colorVariance: 0.18,
    desc: 'Grips wall-slides. Spreads through damp dark. Burns into spore bursts.',
  });

  sludge('LIVING_RESIN', 'Living Resin', {
    color: 0xc98f3a, tags: tags('ORGANIC', 'BIOMASS', 'FLAMMABLE', 'STICKY', 'LIVING'),
    density: 1100, viscosity: 0.75, dispersion: 0, hardness: 1.5, friction: 1.0,
    mass: 1.1, conductivity: 0.12, heatCapacity: 2.2, ignitionTemp: 260,
    burnDuration: 200, ashesInto: 'ASH', bioActivity: 0.4, resonance: 0.3,
    alpha: 0.85, emissive: 0.08, grain: 0.14,
    desc: 'Heat hardens it into petrified wood — the only terrain you can grow.',
  });

  sludge('NANITE_SWARM', 'Nanite Swarm', {
    color: 0x94a6b4, tags: tags('NANITE', 'MACHINE', 'CONDUCTIVE', 'LIVING', 'VOLATILE'),
    density: 1300, viscosity: 0.35, dispersion: 1, hardness: 1, friction: 0.6,
    mass: 1.3, conductivity: 0.5, heatCapacity: 1.6, electricConductivity: 0.6,
    shortCircuits: true, bioActivity: 0.8, reactivity: 1.5, emissive: 0.2,
    alpha: 0.9, grain: 0.3,
    desc: 'Replicates through corpses and machines. Charge disables it briefly.',
  });

  sludge('CURSED_RESIDUE', 'Cursed Residue', {
    color: 0x40204a, tags: tags('CURSED', 'TOXIC', 'STICKY', 'VOID'),
    density: 1250, viscosity: 0.7, dispersion: 0, hardness: 1, friction: 0.95,
    mass: 1.25, temp: 5, conductivity: 0.15, heatCapacity: 2.8, toxicity: 0.6,
    sanctity: -0.85, temporalStability: 0.8, emissive: 0.14, grain: 0.2,
    desc: 'What curses leave behind. Blood empowers it; radiance consumes it.',
  });

  sludge('BIO_SLUDGE', 'Bio Sludge', {
    color: 0x4a6b52, tags: tags('ORGANIC', 'BIOMASS', 'TOXIC', 'STICKY', 'LIVING'),
    density: 1150, viscosity: 0.6, dispersion: 1, hardness: 0.8, friction: 0.9,
    mass: 1.15, conductivity: 0.28, heatCapacity: 3.0, toxicity: 0.55,
    bioActivity: 0.7, ignitionTemp: 380, burnDuration: 100, ashesInto: 'ASH',
    grain: 0.24,
    desc: 'Engineered biomass. Grows toward heat and eats what it reaches.',
  });

  // ---------------------------------------------------- PLASMA / EXOTIC

  // A flame is a cell with a clock, not an effect. Three properties carry that,
  // and all three have to be set together or fire becomes permanent:
  //
  //  - `lifetime` is the clock. cellsim ages `aux` toward it and clears the
  //    cell. FIRE shipped with `lifetime: 0` — "permanent" — and since it has
  //    no `ignitionTemp`, thermal never gives it BURNING and the fuel-timer
  //    burnout never applied either. Ten cells in a sealed room were still ten
  //    3000 ticks later, at 21 °C, still `emissive: 1.0`. One enemy fireball
  //    painted a ceiling orange for the rest of the run.
  //  - `burnDuration` must be 0 for anything that is itself flame. `world.set`
  //    seeds `aux` from `burnDuration`, and cellsim reads that same `aux` as
  //    the lifetime counter — a flame spawned with 45 ticks of "fuel" already
  //    on the clock lives 45 ticks less than its declared lifetime. Zero means
  //    the clock starts where it should. (Fuel belongs to what is burning, not
  //    to the flame: thermal re-seeds `aux` at ignition.)
  //  - a cooled-down target. Heat conduction drags a flame toward ambient in
  //    ~45 ticks; below its threshold it stops being fire and becomes what it
  //    leaves behind. That is the visible tell that a fire is dying, and it
  //    gets a cell out of `emissive: 1.0` the moment it stops being hot.
  plasma('FIRE', 'Fire', {
    lifetime: 130,
    color: 0xff8a2a, tags: tags('PYRO', 'VOLATILE'),
    density: 0.3, viscosity: 0, dispersion: 3, hardness: 0, mass: 0.01,
    temp: 750, conductivity: 0.6, heatCapacity: 0.4, burnTemp: 750,
    burnDuration: 0, emissive: 1.0, alpha: 0.85, grain: 0.5, colorVariance: 0.25,
    condensesAt: 200, condensesInto: 'SMOKE',
    desc: 'Not a special case — just a hot cell that heats its neighbours. It cools into smoke.',
  });

  plasma('HOLY_FIRE', 'Holy Fire', {
    lifetime: 200,
    color: 0xffe27a, tags: tags('PYRO', 'RADIANT', 'SACRED', 'VOLATILE'),
    density: 0.3, viscosity: 0, dispersion: 3, hardness: 0, mass: 0.01,
    temp: 900, conductivity: 0.5, heatCapacity: 0.4, burnTemp: 900,
    burnDuration: 0, emissive: 1.0, sanctity: 1, alpha: 0.8, grain: 0.45,
    condensesAt: 260, condensesInto: 'SANCTIFIED_MIST',
    desc: 'Burns undead and cursed matter without needing fuel. Fades into sanctified mist.',
  });

  // Void flame runs the same clock inverted: it is colder than the room, so it
  // *warms* out of existence rather than cooling out of it. `meltsAt` is the
  // right threshold for something that ends by getting hotter.
  plasma('VOID_FLAME', 'Void Flame', {
    lifetime: 160,
    color: 0x8a3ad4, tags: tags('PYRO', 'VOID', 'VOLATILE'),
    density: 0.4, viscosity: 0, dispersion: 2, hardness: 0, mass: 0.01,
    temp: -200, conductivity: 0.3, heatCapacity: 0.6, burnTemp: -200,
    burnDuration: 0, emissive: 0.7, sanctity: -0.9, temporalStability: 0.5,
    alpha: 0.8, grain: 0.45,
    meltsAt: -20, meltsInto: 'VOID_MIST',
    desc: 'Burns cold. Freezes and erases in the same motion.',
  });

  plasma('PLASMA', 'Plasma', {
    lifetime: 90,
    color: 0x7ae0ff, tags: tags('PYRO', 'CONDUCTIVE', 'VOLATILE', 'MACHINE'),
    density: 0.2, viscosity: 0, dispersion: 4, hardness: 0, mass: 0.01,
    temp: 4000, conductivity: 1.0, heatCapacity: 0.3, burnTemp: 4000,
    burnDuration: 0, emissive: 1.0, electricConductivity: 1.0,
    chargeResistance: 0, reactivity: 2.0, alpha: 0.9, grain: 0.4,
    desc: 'Melts anything meltable. Coolant contact is a pressure catastrophe.',
  });

  plasma('LIGHTNING', 'Lightning', {
    lifetime: 3,
    color: 0xd3ecff, tags: tags('CONDUCTIVE', 'VOLATILE', 'PYRO'),
    density: 0.1, viscosity: 0, dispersion: 6, hardness: 0, mass: 0.005,
    temp: 3000, conductivity: 1.0, heatCapacity: 0.2, burnTemp: 1200,
    burnDuration: 0, emissive: 1.0, electricConductivity: 1.0,
    chargeResistance: 0, alpha: 1, grain: 0.5,
    desc: 'Exists for three ticks. Everything it touches remembers longer.',
  });

  plasma('RADIANT_MATTER', 'Radiant Matter', {
    color: 0xfff7c4, tags: tags('RADIANT', 'SACRED', 'CRYSTAL', 'VOLATILE'),
    density: 900, viscosity: 0.2, dispersion: 2, hardness: 2, friction: 0.5,
    mass: 0.9, temp: 200, conductivity: 0.4, heatCapacity: 2.0,
    emissive: 1.0, sanctity: 1, resonance: 1.0, reactivity: 1.6,
    alpha: 0.8, grain: 0.2,
    desc: 'Solidified light. Purifies corruption; annihilates void on contact.',
  });

  plasma('VOID_MATTER', 'Void Matter', {
    color: 0x1c012c, tags: tags('VOID', 'INSULATOR', 'VOLATILE'),
    density: 1800, viscosity: 0.4, dispersion: 1, hardness: 4, friction: 0.3,
    mass: 1.8, temp: -180, conductivity: 0.01, heatCapacity: 8,
    sanctity: -1, temporalStability: 0.3, reactivity: 1.8, emissive: 0.0,
    alpha: 1, grain: 0.1, colorVariance: 0.04, corrosionResist: 0.9,
    desc: 'Erases matter it touches. Phase movement fails against it.',
  });

  field('TEMPORAL_RESIDUE', 'Temporal Residue', {
    lifetime: 900, burnDuration: 0,
    color: 0xacecea, tags: tags('TEMPORAL', 'CRYSTAL'),
    density: 0.2, viscosity: 0, dispersion: 1, hardness: 0, mass: 0,
    conductivity: 0.3, heatCapacity: 1.0, temporalStability: 0.15,
    resonance: 0.9, emissive: 0.4, alpha: 0.3, grain: 0.35,
    desc: 'Projectiles crossing it arrive late, twice, or slightly before.',
  });

  // -------------------------------------------------- SEMI-SOLID (one-way)
  //
  // Appended after every other material on purpose: declaration order assigns
  // ids, and inserting these into the RIGID block would renumber two thirds of
  // the roster for a cosmetic gain.
  //
  // All three are ordinary `RIGID` materials — honest tags, so the catwalk
  // burns and the grate conducts, inherited from the tag-driven reaction table
  // for free. `oneWaySolid: 'up'` is the only thing that sets them apart.
  //
  // Their densities are the *bulk* density of a lattice that is mostly holes,
  // not the density of the stuff it is made of. That is what lets sand, water
  // and gas fall through a grate in a density-driven cell sim, and it is the
  // right lie to tell: a walkway you cannot see through is a ceiling.
  //
  // **THERE ARE FOUR BECAUSE TWO COULD NOT COVER TWELVE BIOMES.** `docs/24`
  // §6 measured `palette.platform` at 9-11 % of the solid area of every biome
  // in the game — more than `palette.shell` in all twelve — and found seven of
  // them setting a one-way floor within #79's 20/255 step of the rock it is cut
  // into, worst `cathedral_of_salt` at 5/255.
  //
  // The rule the slot is now authored against is **a 20/255 step AND a
  // different 40/255 luminance bin from both bulk slots** (`biomes.ts`
  // `BiomePalette.platform`), and against *that* rule all twelve failed. The two
  // existing materials sit in bins 1 (IRON_GRATE, 70) and 3 (CATWALK, 126), and
  // the roster's bulk luminances cluster hard in bins 1, 2 and 3 — so a
  // **bin 4** one-way floor is what the roster was short of, and six biomes
  // need one. Two materials rather than one because a single pale material in
  // six biomes would be the slot losing its identity, and because a foundry and
  // a rotting wood do not walk on the same thing:
  //
  //     SHELF_FUNGUS  172  rotwood_reach, sunken_vault, chlorine_fen
  //     ZINC_GRATE    184  emberworks, cinder_march, coldforge
  //     CATWALK       126  nanite_reef, hollow_choir, meatworks
  //     IRON_GRATE     70  cathedral_of_salt, verdigris_spire, the_long_hour
  //
  // Three biomes each, four families, and no biome's floor inside its wall's
  // value family any more.

  rigid('CATWALK', 'Wooden Catwalk', {
    color: 0x9a7a4e, tags: tags('FLAMMABLE', 'ORGANIC', 'BIOMASS', 'POROUS', 'INSULATOR'),
    density: 260, hardness: 2, brittleness: 0.6, friction: 0.95, mass: 0.3,
    oneWaySolid: 'up',
    conductivity: 0.1, heatCapacity: 1.3, ignitionTemp: 240, burnTemp: 700,
    burnDuration: 180, ashesInto: 'ASH', corrosionResist: 0.1,
    ruptureThreshold: 120, shatteredInto: 'ASH', alpha: 0.85, grain: 0.32,
    desc: 'Slatted planking. Stand on it, jump up through it, drop off it. Burns like wood, because it is.',
  });

  rigid('IRON_GRATE', 'Iron Grating', {
    color: 0x3e4750, tags: tags('METAL', 'CONDUCTIVE', 'POROUS'),
    density: 620, hardness: 4, brittleness: 0.25, friction: 0.8, mass: 0.8,
    oneWaySolid: 'up',
    conductivity: 0.85, heatCapacity: 1.1, electricConductivity: 0.9,
    chargeResistance: 0.06, corrosionResist: 0.1,
    meltsAt: 1538, meltsInto: 'MOLTEN_IRON',
    ruptureThreshold: 420, shatteredInto: 'RUST_FLAKE', alpha: 0.8, grain: 0.2,
    desc: 'A mesh floor. Everything pours through it and you do not — until you decide to.',
  });

  /**
   * The third one-way floor, for the two biomes neither of the other two can be
   * seen in — and the one place in the roster where the *fiction* was chosen
   * after the luminance rather than before it.
   *
   * A bracket fungus grows in tiers straight out of rotting timber and out of
   * wet masonry, and it is the one structure both the Rotwood Reach and the
   * Sunken Vault would actually have: the Reach's `liquid` is FUNGAL_MAT and
   * both biomes already draw GLOWCAP. Pale sage-green at luminance 172 is 61
   * from the petrified wood, 93 from the wood, 34 from the sandstone and 100
   * from the corpse stone, and it is 80 in `tests/materials.test.ts`'s colour
   * metric from the nearest thing reachable in either biome.
   *
   * Softer than either of the others (hardness 1) because it is a fungus, which
   * makes it the cheapest one-way floor in the game to cut through — that is
   * the trade the two biomes get for having a platform they can see.
   */
  rigid('SHELF_FUNGUS', 'Shelf Fungus', {
    color: 0x8cb987, tags: tags('FLAMMABLE', 'ORGANIC', 'BIOMASS', 'POROUS', 'INSULATOR'),
    density: 180, hardness: 1, brittleness: 0.7, friction: 0.9, mass: 0.2,
    oneWaySolid: 'up',
    conductivity: 0.08, heatCapacity: 1.6, ignitionTemp: 260, burnTemp: 620,
    burnDuration: 140, ashesInto: 'ASH', corrosionResist: 0.05,
    ruptureThreshold: 80, shatteredInto: 'SPORE_DUST', alpha: 0.9, grain: 0.4,
    desc: 'Bracket fungus in tiers off the wall. Stand on it, jump up through it, drop off it — and it burns.',
  });

  /**
   * The fourth, and the industrial half of the same bin.
   *
   * IRON_GRATE is the one-way floor of a place built of dark metal; the
   * Emberworks (RUSTED_IRON 87), the Cinder March (OBSIDIAN 47) and Coldforge
   * (PLASTEEL 85) are places built of *pale* metal and dark rock at once, and
   * against those bulks a dark grate is invisible — 17, 23 and 16 out of 255.
   * Zinc-coated mesh is what those three would actually have: it is the reason
   * real walkway grating is galvanised, and it is the one thing in the
   * Emberworks that the rust has not reached.
   *
   * Non-flammable and corrosion-resistant, which is the whole point of the
   * coating, and it is the one one-way floor that survives its own biome.
   */
  rigid('ZINC_GRATE', 'Galvanised Grating', {
    color: 0xb0bac0, tags: tags('METAL', 'CONDUCTIVE', 'POROUS'),
    density: 640, hardness: 4, brittleness: 0.22, friction: 0.82, mass: 0.85,
    oneWaySolid: 'up',
    conductivity: 0.8, heatCapacity: 1.1, electricConductivity: 0.85,
    chargeResistance: 0.06, corrosionResist: 0.85,
    meltsAt: 1300, meltsInto: 'MOLTEN_IRON',
    ruptureThreshold: 420, shatteredInto: 'RUST_FLAKE', alpha: 0.8, grain: 0.2,
    desc: 'Zinc-dipped mesh. Everything pours through it and you do not — and the rust never took it.',
  });

  finalizeMaterials();
  populateMatIds();
}

/**
 * Numeric id lookups, populated by registerMaterials(). Hot paths read
 * `MAT.WATER` (a plain number property) rather than calling matId().
 *
 * Typed as a record of the known names so a typo is a compile error.
 */
export const MAT = {} as Record<MaterialName, number>;

export type MaterialName = (typeof MATERIAL_NAMES)[number];

export const MATERIAL_NAMES = [
  'VOID',
  // rigid
  'GRANITE', 'LIMESTONE', 'SANDSTONE', 'OBSIDIAN', 'IRON', 'RUSTED_IRON', 'STEEL',
  'BRASS', 'CERAMIC', 'GLASS', 'WOOD', 'PETRIFIED_WOOD', 'CONCRETE', 'PLASTEEL',
  'BONE', 'CHITIN', 'ICE', 'PERMAFROST', 'CRYSTAL', 'VOIDGLASS', 'ADAMANT_SEAL',
  'CIRCUIT_PLATE', 'RUNESTONE', 'CORPSE_STONE',
  'GLOWCAP', 'EMBER_VEIN', 'SIGNAL_BLOOM', 'SAINTLIGHT', 'CHOIRLIGHT',
  // loose
  'SAND', 'GRAVEL', 'ASH', 'BONE_ASH', 'SNOW', 'SOOT', 'COAL_DUST',
  'ALCHEMICAL_DUST', 'RUST_FLAKE', 'SALT', 'SPORE_DUST', 'NANITE_DUST',
  'GRAVE_SOIL', 'EMBER_GRIT',
  // liquid
  'WATER', 'SALTWATER', 'OIL', 'CRUDE_TAR', 'ACID', 'BLOOD', 'CURSED_BLOOD',
  'LAVA', 'MOLTEN_IRON', 'MERCURY', 'REACTOR_COOLANT', 'MACHINE_OIL', 'ALCOHOL',
  'BRINE', 'PLAGUE_SLURRY', 'LIQUID_VOID', 'RADIANT_ICHOR', 'CHRONO_FLUID',
  // gas
  'STEAM', 'SMOKE', 'TOXIC_SMOKE', 'CHLORINE', 'METHANE', 'SPORE_CLOUD',
  'VOID_MIST', 'SANCTIFIED_MIST', 'NANITE_FOG', 'FLASH_VAPOR',
  // sludge
  'MUD', 'TAR', 'FLESH', 'ROTTEN_FLESH', 'FUNGAL_MAT', 'LIVING_RESIN',
  'NANITE_SWARM', 'CURSED_RESIDUE', 'BIO_SLUDGE',
  // plasma / field
  'FIRE', 'HOLY_FIRE', 'VOID_FLAME', 'PLASMA', 'LIGHTNING', 'RADIANT_MATTER',
  'VOID_MATTER', 'TEMPORAL_RESIDUE',
  // semi-solid (one-way floors) — listed last because they are declared last
  'CATWALK', 'IRON_GRATE', 'SHELF_FUNGUS', 'ZINC_GRATE',
] as const;

function populateMatIds(): void {
  for (const name of MATERIAL_NAMES) {
    const id = tryMatId(name);
    if (id === undefined) {
      throw new Error(`MATERIAL_NAMES lists "${name}" but it was never defined`);
    }
    MAT[name] = id;
  }
  // Catch the reverse mismatch too: a material defined but not listed would be
  // invisible to MAT, which is exactly the kind of bug that shows up as a
  // mysterious `undefined` material id three systems away.
  if (materialCount() !== MATERIAL_NAMES.length) {
    throw new Error(
      `material count mismatch: ${materialCount()} defined, ${MATERIAL_NAMES.length} listed`,
    );
  }
}
